import { useState } from 'react';
import { timesheetAPI } from '../../../../services/api';

export interface TimesheetEntry {
  id: string;
  projectId?: string;
  project_id?: string;
  projectName?: string;
  employeeId?: string;
  employee_id?: string;
  employeeName?: string;
  employee_name?: string;
  employeeEmail?: string;
  employee_email?: string;
  role?: string;
  dateWorked?: string;
  date_worked?: string;
  hours: number;
  billable?: boolean;
  cost?: number;
  notes?: string;
  milestoneId?: string;
  milestone_id?: string;
  hourlyRate?: number;
  hourly_rate?: number;
}

export interface TimesheetSummary {
  totalHours?: number;
  total_hours?: number;
  billableHours?: number;
  nonBillableHours?: number;
  totalCost?: number;
  total_cost?: number;
  entryCount?: number;
  entry_count?: number;
}

type ApiFilters = Record<string, string>;

interface LoadTimesheetsOptions {
  selectedProject?: { id: string; name: string } | null;
  selectedPerson?: { id: string } | null;
  filters?: {
    startDate?: string;
    endDate?: string;
    personId?: string;
  };
  projects?: Array<{ id: string; name: string }>;
}

interface ApiResult {
  success: boolean;
  data?: TimesheetEntry[];
  summary?: TimesheetSummary;
  error?: string;
}

export const useTimesheets = () => {
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([]);
  const [summary, setSummary] = useState<TimesheetSummary>({});
  const [loading, setLoading] = useState(false);

  const loadTimesheets = async (options: LoadTimesheetsOptions = {}): Promise<void> => {
    const { selectedProject, selectedPerson, filters, projects } = options;

    setLoading(true);
    try {
      const apiFilters: ApiFilters = {};
      if (filters?.startDate) apiFilters['start_date'] = filters.startDate;
      if (filters?.endDate) apiFilters['end_date'] = filters.endDate;
      if (filters?.personId) apiFilters['person_id'] = filters.personId;

      if (selectedProject) {
        const result: ApiResult = await timesheetAPI.getProjectTimesheets(selectedProject.id, apiFilters);
        if (result.success) {
          setTimesheets(result.data ?? []);
          setSummary(result.summary ?? {});
        } else {
          console.error('Error loading timesheets:', result.error);
          setTimesheets([]);
          setSummary({});
        }
      } else if (projects) {
        const allTimesheets: TimesheetEntry[] = [];
        const combinedSummary: TimesheetSummary = {
          totalHours: 0,
          billableHours: 0,
          nonBillableHours: 0,
          totalCost: 0
        };

        const projectPromises = projects.map(async (project) => {
          try {
            const result: ApiResult = await timesheetAPI.getProjectTimesheets(project.id, apiFilters);
            if (result.success && result.data) {
              const projectTimesheets = result.data.map(ts => ({
                ...ts,
                projectName: project.name,
                projectId: project.id
              }));
              allTimesheets.push(...projectTimesheets);

              if (result.summary) {
                combinedSummary.totalHours = (combinedSummary.totalHours ?? 0) + (result.summary.totalHours ?? 0);
                combinedSummary.billableHours = (combinedSummary.billableHours ?? 0) + (result.summary.billableHours ?? 0);
                combinedSummary.nonBillableHours = (combinedSummary.nonBillableHours ?? 0) + (result.summary.nonBillableHours ?? 0);
                combinedSummary.totalCost = (combinedSummary.totalCost ?? 0) + (result.summary.totalCost ?? 0);
              }
            }
          } catch (err: unknown) {
            console.error(`Error loading timesheets for project ${project.id}:`, err);
          }
        });

        await Promise.all(projectPromises);
        setTimesheets(allTimesheets);
        setSummary(combinedSummary);
      } else if (selectedPerson) {
        const personFilters: ApiFilters = { ...apiFilters };
        delete personFilters['person_id'];
        personFilters['person_id'] = selectedPerson.id;
        const result: ApiResult = await timesheetAPI.getProjectTimesheets('all', personFilters);
        if (result.success) {
          setTimesheets(result.data ?? []);
          setSummary(result.summary ?? {});
        } else {
          console.error('Error loading timesheets:', result.error);
          setTimesheets([]);
          setSummary({});
        }
      }
    } catch (err: unknown) {
      console.error('Error loading timesheets:', err);
      setTimesheets([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  const addTimesheet = async (timesheetData: Partial<TimesheetEntry> & { projectId?: string }): Promise<ApiResult> => {
    const projectId = timesheetData.projectId;
    if (!projectId) {
      return { success: false, error: 'Project ID is required' };
    }
    return timesheetAPI.addTimesheetEntry(projectId, timesheetData);
  };

  const updateTimesheet = async (timesheetId: string, updates: Partial<TimesheetEntry> & { projectId?: string; project_id?: string }): Promise<ApiResult> => {
    const projectId = updates.projectId || updates.project_id;
    if (!projectId) {
      return { success: false, error: 'Project ID is required' };
    }
    return timesheetAPI.updateTimesheetEntry(projectId, timesheetId, updates);
  };

  const deleteTimesheet = async (timesheetId: string, projectId: string): Promise<ApiResult> => {
    if (!projectId) {
      return { success: false, error: 'Project ID is required' };
    }
    return timesheetAPI.deleteTimesheetEntry(projectId, timesheetId);
  };

  return {
    timesheets,
    summary,
    loading,
    loadTimesheets,
    addTimesheet,
    updateTimesheet,
    deleteTimesheet
  };
};
